<template lang="pug">
    layout.contentTransparent(fluid)

        // Title
        title-component(
            :title="$t('users.list.title')"
            slot="title"
            type="h2"
        )

        template
            v-layout(row wrap justify-space-between)
                v-flex(xs12 lg3)
                    div.c-form

                        // Search field
                        v-text-field(
                            :label="$t('fields.search')"
                            v-model="search"
                            append-icon="search"
                            class="search-input"
                            outline
                        )

                v-flex(xs12 lg7 offset-lg2)
                    div.controlsWrapper(v-if="showControls")
                        div

                            // Crete template button
                            button-component.controlsButton(
                                v-if="isChampion"
                                :action="createTemplate"
                                :isLoading="isLoading"
                                :disabled="isLoading"
                                color="action"
                                large
                            ) {{ $t('users.list.createButton') }}

                            div(v-if="!isMobile && isChampion") {{ $t('users.list.createButtonDescription') }}

                        div

                            // Import users button
                            upload-button.controlsButton(
                                v-if="isChampion"
                                :isLoading="isLoading"
                                :disabled="isLoading"
                                :selectedCallback="uploadDocument"
                            )
                                span {{ $t('users.list.importButton') }}
                                v-icon.iconImport.ml-1 trending_flat

                            div(v-if="!isMobile && isChampion") {{ $t('users.list.importButtonDescription') }}

                        div

                            // Export users button
                            button-component.controlsButton(
                                v-if="isChampion"
                                :action="exportUsersByParams"
                                :isLoading="isLoading"
                                :disabled="noUsers"
                                color="action"
                                large
                            )
                                span {{ $t('users.list.exportButton') }}
                                v-icon.iconExport.mr-1 trending_flat

                            div(v-if="!isMobile && isChampion") {{ $t('users.list.exportButtonDescription') }}

                        div

                            // Crete user button
                            button-component.controlsButton(
                                :action="createUser"
                                :isLoading="isLoading"
                                :disabled="isLoading"
                                color="action"
                                large
                            ) {{ $t('users.list.createUserButton') }}

            separator-component.overlay

            v-layout(row wrap)

                // Skeleton rows — initial fetch only. Mirrors the row
                // geometry of ListItem.vue so swapping placeholders for
                // real rows doesn't shift the layout.
                v-flex(xs12 v-if="isFetching && !items.length")
                    div.userSkeleton(v-for="n in skeletonCount" :key="`user-skel-${n}`")
                        div.userSkeleton__cell.userSkeleton__cell--primary.shimmer
                        div.userSkeleton__cell.userSkeleton__cell--secondary.shimmer
                        div.userSkeleton__cell.userSkeleton__cell--meta.shimmer
                        div.userSkeleton__cell.userSkeleton__cell--badge.shimmer
                        div.userSkeleton__cell.userSkeleton__cell--actions.shimmer

                v-flex(xs12 v-else-if="filterParams")

                    // Users list (Mobile)
                    v-data-table.c-table(
                        v-if="isMobile"
                        :headers-length="7"
                        :items="items"
                        :loading="isFetching"
                        :pagination.sync="pagination"
                        :total-items="total"
                        :rowsPerPageItems="[10]"
                    )
                        template(
                            v-slot:items="props"
                        )
                            tr(@click="props.expanded = !props.expanded")
                                list-mobile(
                                    :item="props.item"
                                    :expanded="props.expanded"
                                    @removed="resetPagination"
                                )

                        template(slot="expand" slot-scope="props")
                            tr
                                td(colspan="6")
                                    expand-card(:item="props.item" :customFields="model")

                    // Users list (Desktop)
                    v-data-table.c-table.visibleHeader(
                        v-if="!isMobile"
                        :headers="headers"
                        :items="items"
                        :loading="isFetching"
                        :pagination.sync="pagination"
                        :total-items="total"
                        :rowsPerPageItems="[10]"
                    )
                        template(v-slot:headers="props")
                            tr
                                th(v-for="header in props.headers" :key="header.text")

                                    div.text-left(v-if="header.filter && header.isCustom")
                                        filter-component(
                                            :items="customFilterItems(props.header.filter)"
                                            :name="header.text"
                                            :callbackFunction="customFiltersCallback"
                                            :filterName="header.filter"
                                        )
                                    div.text-left(v-else-if="header.filter")
                                        filter-component(
                                            :items="filterItems(header.filter)"
                                            :name="header.text"
                                            :callbackFunction="filtersCallback"
                                            :filterName="header.value"
                                        )

                                    div.text-left(v-else) {{ `${header.text}  ` }}

                        template(v-slot:items="props")
                            list-item(:item="props.item" @removed="resetPagination")

        controls-mobile(
            v-if="isMobile"
            :sortHandler="handleMobileSort"
            :filterHandler="filtersCallback"
            :filters="filterParams"
        )

        errors-list
</template>

<script>
    import Layout from '../../Layout/Layout';
    import ListItem from './ListItem';
    import ListMobile from './ListItemMobile';
    import ExpandCard from './ExpandCard';
    import ErrorsList from './ErrorsList';
    import ControlsMobile from './ControlsMobile';
    import VueTypes from 'vue-types';
    import users from '~api/users';
    import Button from '~components/Button';
    import Separator from '~components/Title';
    import Title from '~components/Separator';
    import Filter from '~components/Filter';
    import UploadButton from '~components/UploadButton';
    import { mapFields } from 'vuex-map-fields';
    import { mapActions, mapGetters } from 'vuex';

    export default {
        components: {
            Layout,
            ListItem,
            ErrorsList,
            ListMobile,
            ExpandCard,
            UploadButton,
            ControlsMobile,
            TitleComponent: Title,
            ButtonComponent: Button,
            FilterComponent: Filter,
            SeparatorComponent: Separator
        },
        props: {
            id: VueTypes.string.def('')
        },
        data() {
            return {
                filters: {
                    isActive: null,
                    city: null,
                    state: null,
                    country: null,
                    department: null,
                    businessName: null
                },
                filterParams: null,
                items: [],
                total: 0,
                rowsPerPage: [10],
                pagination: {
                    descending: true
                },
                params: null,
                search: null,
                timeout: null,
                routeInitialized: false,
                isFetching: false
            };
        },
        computed: {
            ...mapFields('document', {
                model: 'fields'
            }),
            ...mapGetters('document', ['isLoading']),
            ...mapGetters('auth', ['showDepartment', 'isChampion', 'isAdmin']),

            // Number of skeleton rows to show during the initial fetch.
            // One full page worth so the placeholder block roughly
            // matches the table that's about to land.
            skeletonCount() {
                return 10;
            },

            /**
             * Check screen resolution
             */
            isMobile() {
                return this.$vuetify.breakpoint.smAndDown;
            },

            /**
             * Show controls by role
             */
            showControls() {
                return !!(this.isChampion || this.isAdmin);
            },

            /**
             * Table headers
             */
            headers() {
                let headersChampionFields;

                if (this.isChampion) {
                    headersChampionFields = [
                        ...this.$t('users.tableHeadersChampion')
                    ];

                    if (!this.showDepartment) {
                        return headersChampionFields.filter(
                            item => item.value !== 'department'
                        );
                    }

                    return headersChampionFields;
                }

                return this.$t('users.tableHeadersAdmin');
            },
            noUsers() {
                return this.items.length === 0;
            }
        },
        watch: {
            /**
             * Watch search and set pagination param on change
             */
            search(value) {
                if (this.pagination.search === value) return;

                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => {
                    this.pagination = {
                        ...this.pagination,
                        search: value
                    };
                }, 500);

                this.updateQueryParam(value);
            },

            /**
             * Watch pagination and update managers data on change
             */
            pagination: {
                async handler(paginationData) {
                    if (!this.routeInitialized) {
                        this.pagination.sortBy = null;
                        return;
                    }

                    // Collect params.
                    this.params = {
                        page: paginationData.page,
                        search: paginationData.search,
                        sortBy: paginationData.sortBy,
                        orderBy: paginationData.descending ? 'desc' : 'asc'
                    };

                    // Collect filter params
                    Object.keys(this.filters).map(key => {
                        if (this.filters[key] !== null)
                            return (this.params[key] = this.filters[key]);
                    });

                    if (this.id && this.id.length) {
                        this.params.businessId = this.id;
                    }

                    if (
                        this.params.customFields &&
                        this.params.customFields.length
                    ) {
                        this.params.customFields = JSON.stringify(
                            this.params.customFields
                        );
                    }

                    this.isFetching = true;
                    try {
                        const { data } = await users.list(this.params);

                        this.items = data.items;
                        this.total =
                            data.meta && data.meta.total ? data.meta.total : 0;
                    } finally {
                        this.isFetching = false;
                    }
                },
                deep: true
            }
        },

        // Initial fetch happens here (rather than beforeRouteEnter) so
        // the page renders immediately with skeleton rows while the
        // first list request is in flight.
        async created() {
            if (!((this.id && this.id.length) || this.isChampion)) {
                this.model = [];
            }

            if (this.$route.query.search) {
                this.search = this.$route.query.search;
            }

            this.isFetching = true;
            try {
                const { data } = await users.list({
                    businessId: this.id,
                    search: this.search
                });

                this.items = data.items;
                this.total = data.meta && data.meta.total ? data.meta.total : 0;
                this.filterParams = { ...data.filters };

                const initialPagination = {
                    page: data.meta && data.meta.page ? data.meta.page : 0,
                    rowsPerPage:
                        data.meta && data.meta.limit ? data.meta.limit : 0
                };

                if (this.search) {
                    initialPagination.search = this.search;
                }

                this.pagination = {
                    ...this.pagination,
                    ...initialPagination
                };

                this.$nextTick(() => {
                    this.routeInitialized = true;
                });
            } finally {
                this.isFetching = false;
            }
        },
        methods: {
            ...mapActions('document', ['importUsers', 'exportUsers']),

            updateQueryParam(value) {
                if (this.$route.query.search === value) return;
                this.$router.replace({
                    query: {
                        ...this.$route.query,
                        search: value
                    }
                });
            },

            /**
             * Mobile sorting handler
             */
            handleMobileSort(name, e) {
                this.pagination.sortBy = name;
                this.pagination.descending = e;
                this.resetPagination();
            },

            /**
             * Export users with selected params
             */
            exportUsersByParams() {
                this.exportUsers(this.params);
            },

            /**
             * Items for filter
             */
            filterItems(filter) {
                if (typeof filter === 'object') return filter;
                const customFilters = this.filterParams[filter].map(item => {
                    return { title: item, value: item };
                });

                return [
                    {
                        title: 'All',
                        value: null
                    },
                    ...customFilters
                ];
            },

            /**
             * Items for custom filter
             */
            customFilterItems(filter) {
                const customFilters = this.filterParams.customFields[
                    filter
                ].map(item => {
                    return { title: item, value: item };
                });

                customFilters[0] = {
                    title: 'All',
                    value: null
                };

                return customFilters;
            },

            /**
             * Reset pagination.
             */
            resetPagination() {
                this.pagination = { ...this.pagination };
            },

            /**
             * Redirect to save custom fields and create template page
             */
            createTemplate() {
                this.$router.push({ name: 'dashboard.document.crete' });
            },

            /**
             * Redirect to save custom fields and create template page
             */
            createUser() {
                this.$router.push({ name: 'dashboard.users.create' });
            },

            /**
             * Get filter param on change status filter
             * @param {string} e
             */
            filtersCallback(e) {
                this.filters[e.name] = e.value;
                this.resetPagination();
            },

            /**
             * Get filter param on change status filter
             * @param {string} e
             */
            customFiltersCallback(e) {
                if (this.filters.customFields) {
                    this.filters.customFields = [
                        { id: e.name, value: e.value },
                        this.filters.customFields.find(
                            item => item.id !== e.name
                        )
                    ];
                }

                this.filters.customFields = [{ id: e.name, value: e.value }];

                if (e.value === null) {
                    this.filters.customFields = this.filters.customFields.filter(
                        item => item.id !== e.name
                    );
                }

                this.resetPagination();
            },

            /**
             * Change sort in table column
             * @param column
             */
            changeSort(column) {
                if (this.pagination.sortBy === column) {
                    this.pagination.descending = !this.pagination.descending;
                } else {
                    this.pagination.sortBy = column;
                    this.pagination.descending = false;
                }
            },

            /**
             * Upload document with users data
             * @param e
             * @returns {Promise<void>}
             */
            async uploadDocument(e) {
                let formData = new FormData();
                formData.append('name', e.name);
                formData.append('file', e);

                const importResp = await this.importUsers(formData);

                if (importResp) {
                    const resp = await users.list();
                    this.items = resp.data.items;
                    this.filterParams = { ...resp.data.filters };

                    if (this.isChampion) {
                        this.updateUserData(
                            this.filterParams.departments || []
                        );
                    }

                    this.resetPagination();
                }
            },

            /**
             * Update user data in local storage
             *
             * @param departments
             */
            updateUserData(departments) {
                let user = JSON.parse(localStorage.getItem('user'));

                if (user.business) {
                    user.business.userDepartments = departments;
                }
                window.localStorage.setItem('user', JSON.stringify(user));
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl'

    // Skeleton rows shown during the initial users.list fetch. Cells
    // approximate a real row's column widths so layout doesn't jump
    // when items arrive.
    .userSkeleton
        display: flex
        align-items: center
        gap: 16px
        padding: 14px 16px
        border-bottom: 1px solid rgba(0, 0, 0, 0.06)

    .userSkeleton__cell
        height: 14px
        border-radius: 4px

    .userSkeleton__cell--primary
        flex: 0 0 18%

    .userSkeleton__cell--secondary
        flex: 0 0 22%

    .userSkeleton__cell--meta
        flex: 1 1 auto

    .userSkeleton__cell--badge
        flex: 0 0 64px
        height: 18px
        border-radius: 9px

    .userSkeleton__cell--actions
        flex: 0 0 115px

    // Shimmer pass — soft moving highlight tells the user "loading,
    // not just empty." Same animation as the feed skeleton.
    .shimmer
        position: relative
        overflow: hidden
        background-color: rgba(0, 0, 0, 0.08)

        &:after
            content: ''
            position: absolute
            top: 0
            left: 0
            right: 0
            bottom: 0
            transform: translateX(-100%)
            background: linear-gradient(
                90deg,
                transparent,
                rgba(255, 255, 255, 0.5),
                transparent
            )
            animation: shimmer 1.4s infinite

    @keyframes shimmer
        100%
            transform: translateX(100%)

    .controlsWrapper
        display: flex
        justify-content: flex-end

        & > div
            flex: 1 1 33.3%

            &:not(:last-child)
                padding-right: 16px

        .controlsButton
            width: 100%
            margin: 0 0 8px

    .text-left
        text-align: left

    .ceilText
        display: inline-block

    .createButtonWrapper
        .btn
            margin-top: 0
            margin-bottom: 0

    .iconImport
    .iconExport
        font-size: 18px

    .iconImport
        transform: rotate(-90deg)

    .iconExport
        transform: rotate(90deg)

    @media screen and (max-width: $grid-breakpoints.md - 1)
        .controlsWrapper
            flex-direction: column

            & > div:not(:last-child)
                padding-right: 0
                margin-bottom: 16px

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .userSkeleton
            gap: 10px
            padding: 12px

        .userSkeleton__cell--meta
            display: none

        .userSkeleton__cell--actions
            flex: 0 0 80px

        .createButtonWrapper
            display: block

            .btn
                width: 100%
                margin-bottom: 16px
                margin-right: 0

        .controlsWrapper
            & > div:not(:last-child)
                margin-bottom: 2px
</style>
